import {DateTime} from "luxon";
import type {EBirdChecklistByFamily, EBirdMyDataSchema, EBirdSightingsCalibrationResult} from "ebird-mydata-reader";
import {buildSpeciesDetailPaths} from "../speciesDetailPaths";
import {getObservationYears, getYearSpeciesKey, getYearSpeciesObservations} from "../yearSpeciesCounts";
import {UPCOMING_TARGET_DUE_NOW_DAYS, UPCOMING_TARGET_HORIZON_DAYS, UPCOMING_TARGET_HOME_RADIUS_MILES, UPCOMING_TARGET_LIMIT, UPCOMING_TARGET_MIN_LOCAL_YEARS} from "./constants";
import type {UpcomingTarget, UpcomingTargetsSummary} from "./types";

type HistoricalFirstSighting = {
    year: number,
    observation: EBirdMyDataSchema
};

type LocalSighting = {
    date: string,
    year: number,
    location?: string
};

type HomeLocationLike = {
    latitude: number,
    longitude: number
};

const getDistanceMiles = (start: HomeLocationLike, end: EBirdMyDataSchema) => {
    if (typeof end.latitude !== 'number' || typeof end.longitude !== 'number') return Number.POSITIVE_INFINITY;
    const earthRadiusMiles = 3958.8;
    const toRadians = (value: number) => value * Math.PI / 180;
    const latitudeDelta = toRadians(end.latitude - start.latitude);
    const longitudeDelta = toRadians(end.longitude - start.longitude);
    const startLatitude = toRadians(start.latitude);
    const endLatitude = toRadians(end.latitude);
    const haversine = Math.sin(latitudeDelta / 2) ** 2 + Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDelta / 2) ** 2;
    return 2 * earthRadiusMiles * Math.asin(Math.sqrt(haversine));
};

const getYearDay = (date?: string) => {
    const parsed = DateTime.fromISO(date ?? '');
    return parsed.isValid ? parsed.ordinal : undefined;
};

const getMedian = (values: number[]) => {
    const ordered = [...values].sort((left, right) => left - right);
    return ordered[Math.floor(ordered.length / 2)];
};

const getExpectedDate = (ordinal: number, currentYear: number) => DateTime.local(currentYear).startOf('year').plus({days: ordinal - 1});

export const buildUpcomingTargets = (
    rawData: EBirdMyDataSchema[],
    checklist: EBirdChecklistByFamily[],
    calibrationResult?: EBirdSightingsCalibrationResult,
    now = DateTime.local(),
    homeLocation?: HomeLocationLike
): UpcomingTargetsSummary => {
    const currentYear = now.year;
    const observationYears = getObservationYears(rawData);
    const historicalYears = observationYears.filter(year => year < currentYear && year > 1900);
    const currentYearSpecies = new Set<string | number>(
        observationYears.includes(currentYear)
            ? getYearSpeciesObservations(rawData, currentYear, calibrationResult).map(getYearSpeciesKey).filter(Boolean)
            : []
    );
    const firstSightingsBySpecies = new Map<string | number, HistoricalFirstSighting[]>();
    const localSightingsBySpecies = new Map<string | number, LocalSighting[]>();
    if (!homeLocation) {
        return {dueNow: [], comingSoon: [], historicalYears: historicalYears.length, currentYear, hasHomeLocation: false};
    }

    for (const year of historicalYears) {
        const firstSightingsThisYear = new Map<string | number, EBirdMyDataSchema>();
        const countableYearObservations = getYearSpeciesObservations(rawData, year, calibrationResult);
        const countableSpecies = new Set(countableYearObservations.map(getYearSpeciesKey).filter(Boolean));
        const localYearObservations = rawData
            .filter(observation => Number(observation.date?.slice(0, 4)) === year)
            .filter(observation => countableSpecies.has(getYearSpeciesKey(observation)))
            .filter(observation => getDistanceMiles(homeLocation, observation) <= UPCOMING_TARGET_HOME_RADIUS_MILES)
            .filter((observation): observation is EBirdMyDataSchema & {date: string} => Boolean(observation.date))
            .sort((left, right) => `${left.date} ${left.time ?? ''}`.localeCompare(`${right.date} ${right.time ?? ''}`));
        for (const observation of localYearObservations) {
            const speciesKey = getYearSpeciesKey(observation);
            if (!speciesKey || !observation.date) continue;
            localSightingsBySpecies.set(speciesKey, [
                ...(localSightingsBySpecies.get(speciesKey) ?? []),
                {date: observation.date, year, location: observation.location}
            ]);
            if (firstSightingsThisYear.has(speciesKey)) continue;
            firstSightingsThisYear.set(speciesKey, observation);
        }

        for (const [speciesKey, observation] of Array.from(firstSightingsThisYear.entries())) {
            firstSightingsBySpecies.set(speciesKey, [
                ...(firstSightingsBySpecies.get(speciesKey) ?? []),
                {year, observation}
            ]);
        }
    }

    const speciesDetailPaths = buildSpeciesDetailPaths(checklist);
    const candidates: Array<UpcomingTarget & {daysUntil: number}> = [];
    const horizonEnd = now.startOf('day').plus({days: UPCOMING_TARGET_HORIZON_DAYS});

    for (const [speciesKey, sightings] of Array.from(firstSightingsBySpecies.entries())) {
        if (currentYearSpecies.has(speciesKey)) continue;

        if (sightings.length < UPCOMING_TARGET_MIN_LOCAL_YEARS) continue;
        const expectedOrdinal = getMedian(sightings.map(sighting => getYearDay(sighting.observation.date)).filter((day): day is number => day !== undefined));
        if (!expectedOrdinal) continue;

        const expectedDate = getExpectedDate(expectedOrdinal, currentYear);
        const daysUntil = Math.floor(expectedDate.startOf('day').diff(now.startOf('day'), 'days').days);
        if (daysUntil < 0 || expectedDate > horizonEnd) continue;

        const mostRecentSighting = sightings[sightings.length - 1];
        const observation = mostRecentSighting.observation;
        const recentSightings = (localSightingsBySpecies.get(speciesKey) ?? [])
            .sort((left, right) => right.date.localeCompare(left.date))
            .slice(0, 4);
        candidates.push({
            speciesKey,
            taxonomicOrder: observation.reportAsTaxonomicOrder ?? observation.canonicalTaxonomicOrder ?? observation.taxonomicOrder,
            commonName: observation.commonName,
            scientificName: observation.scientificName,
            expectedDate: expectedDate.toISODate() ?? observation.date,
            timing: daysUntil <= UPCOMING_TARGET_DUE_NOW_DAYS ? 'due-now' : 'coming-soon',
            yearsSeen: sightings.length,
            historicalYears: historicalYears.length,
            lastSeenYear: mostRecentSighting.year,
            recentSightings,
            detailPath: speciesDetailPaths[observation.reportAsTaxonomicOrder ?? observation.canonicalTaxonomicOrder ?? observation.taxonomicOrder ?? ''],
            daysUntil
        });
    }

    const rankTargets = (targets: Array<UpcomingTarget & {daysUntil: number}>) => targets
        .sort((left, right) => right.yearsSeen - left.yearsSeen || right.lastSeenYear - left.lastSeenYear || left.daysUntil - right.daysUntil || left.commonName.localeCompare(right.commonName))
        .slice(0, UPCOMING_TARGET_LIMIT)
        .map(({daysUntil, ...target}) => target);

    return {
        dueNow: rankTargets(candidates.filter(target => target.timing === 'due-now')),
        comingSoon: rankTargets(candidates.filter(target => target.timing === 'coming-soon')),
        historicalYears: historicalYears.length,
        currentYear,
        hasHomeLocation: true
    };
};
